
import React, { useState, useEffect, forwardRef, useMemo, useCallback } from "react";
import { Spin, Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout';
import _ from 'lodash';
import { SettingIcon, PlayIcon, DeleteIcon, EditIcon } from "components/Icons";
import BaseModal from 'components/BaseModal';
import { FieldTypes } from "utils/formUtils";
import AppLoader from 'components/AppLoader';
import { generateFormFields } from "utils/formUtils";
import Vocabulary from 'components/Vocabulary';
import message from 'components/CustomMessage';
import CIConfigAPI from "restAPI/CIConfigAPI";
import ConfirmPopup from 'components/ConfirmPopup';
import ModalUtil from "utils/modalUtil";
import { parseSelectOptions } from 'utils/formUtils';
import { PermissionSetMap } from "constants/RBACConstants";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";

const CI_CONSTANT = {
  TOKEN_AUTH: "TOKEN",
  BASIC_AUTH: "BASIC_AUTH",
  GET_REQUEST: "GET",
  POST_REQUEST: "POST"
}
const getInitialValues = (data, requestOptions) => {
  let initialValues = {};
  if (!_.isEmpty(data)) {
    const { product_id, sub_product_id, environment  } = data;
    initialValues = {
      httpMethod: requestOptions[0].value,
      productId: product_id,
      subProductId: sub_product_id,
      environment: environment,
      authType: CI_CONSTANT.TOKEN_AUTH,
      url: '',
      token: '',
      username: '',
      password: ''
    }
  }
  return initialValues
}

const getRequestOptions = () => ([{label: CI_CONSTANT.GET_REQUEST, value: CI_CONSTANT.GET_REQUEST}, {label: CI_CONSTANT.POST_REQUEST, value: CI_CONSTANT.POST_REQUEST}]);
const getAuthOptions = () => ([{label: 'Basic Auth', value: CI_CONSTANT.BASIC_AUTH}, {label: 'Token', value: CI_CONSTANT.TOKEN_AUTH}]);

const CIConfiguration = forwardRef(function({afterClose = _.noop, data={}, ...props}, ref) {
  const [productList, setProductList] = useState([]);
  const [subProductList, setSubProductList] = useState([]);
  const [environmentList, setEnvironmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = useForm();
  const [requestOptions] = useState(getRequestOptions);
  const [authOptions] = useState(getAuthOptions)
  const [initialFormValues] = useState(() => getInitialValues(data, requestOptions));
  const [requestType, setRequestType] = useState(CI_CONSTANT.GET_REQUEST)

  useEffect(() => {
    if (!_.isEmpty(data)) {
      const { product_name, product_id, sub_product_name, sub_product_id, environment } = data;
      setProductList([{label: product_name, value: product_id}]);
      setSubProductList([{label: sub_product_name , value: sub_product_id }]);
      setEnvironmentList([{label: <EnvironmentName name={environment} />, value: environment}]);
    }
  }, [data, form]);

  useEffect(() => {
    if (data?.ciJobConfigId) {
      setLoading(true);
      try {
        const {promise, cancel} = CIConfigAPI.getCIConfigJob({subProductId: data.sub_product_id, env: data.environment});
        promise.then(res => {
          form.setFieldsValue({
            url: res.url,
            username: res.username,
            body: res.body,
            authType: res.authType,
            httpMethod: res.httpMethod
          });
          setRequestType(res.httpMethod)
          setLoading(false);
        })
        return cancel;
      } catch (e) {
        setLoading(false);
        console.log(e)
      }
    }
  }, [data, data.ciJobConfigId, form, data.sub_product_id, data.environment])

  const onSubmit = useCallback(
    async () => {
      const valid = await form.validateFields();
        if (valid) {
        setLoading(true);
        try {
          const formData = form.getFieldsValue();
          const method = data.ciJobConfigId ? 'PUT' : 'POST';
          if (data.id) {
            formData.toolConfigId = data.id;
          }

          if (data.ciJobConfigId) {
            formData.id = data.ciJobConfigId;
          }
          if (requestType === CI_CONSTANT.POST_REQUEST) {
            delete formData.token;
            if(data.ciJobConfigId && !formData.password) {
              delete formData.password
            }
          } else {
            delete formData.username;
            delete formData.password;
            if(data.ciJobConfigId && !formData.token) {
              delete formData.token
            }
          }
          delete formData.blankField;
          await CIConfigAPI.saveCIConfigJob(formData, method).promise.then(() => {
              setLoading(false);
              message.success(`CI ${data.ciJobConfigId ? 'Updated' : 'configured'} successfully.`);
          });
        } catch (e) {
          setLoading(false);
          console.error(e);
        }
      }
  }, [form, data, requestType])

  const onChange = useCallback((values) => {
    if(values.httpMethod) {
      setRequestType(values.httpMethod)
      form.setFieldsValue({
        authType: values.httpMethod === CI_CONSTANT.GET_REQUEST ? CI_CONSTANT.TOKEN_AUTH : CI_CONSTANT.BASIC_AUTH,
      })
    }
  }, [form]);

  const productVocab = useMemo(() => (<Vocabulary name="product" />), []);
  const subProductVocab = useMemo(() => (<Vocabulary name="subProduct" />), []);

  const fields = useMemo(() => {
    return [
      {
        name: 'productId',
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        options: productList,
        label: productVocab,
        disabled: true
      },
      {
        name: 'subProductId',
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        options: subProductList,
        label: subProductVocab,
        disabled: true
      },
      {
        name: 'environment',
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        label: 'Environment',
        options: parseSelectOptions(environmentList),
        disabled: true,
      },
      {
        name: 'httpMethod',
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        label: 'Request Type',
        options: requestOptions,
        props: {
          autoFocus: true
        }
      },
      {
        name: 'authType',
        type: FieldTypes.SELECT,
        colSpan: 12,
        required: true,
        label: 'Auth Type',
        options: authOptions,
        disabled: true
      },
      ...(requestType === CI_CONSTANT.POST_REQUEST
        ? [
          {
            name: 'blankField',
            colSpan: 12,
            type: FieldTypes.LABEL
          },
          {
            name: 'username',
            colSpan: 12,
            required: true,
            label: 'User Name',
          },
          {
            name: 'password',
            colSpan: 12,
            required: !data.ciJobConfigId,
            type: FieldTypes.PASSWORD,
            label: 'Password',
          }
        ]
        : [
          {
            name: 'token',
            colSpan: 12,
            required: !data.ciJobConfigId,
            type: FieldTypes.PASSWORD,
            label: 'Token',
          }
        ]),
        {
          name: 'url',
          colSpan: 12,
          required: true,
          label: 'Url'
        }, ...(requestType === 'POST' ? [{
          name: 'body',
          colSpan: 12,
          required: true,
          label: 'Body'
        }]: []),

    ]
  }, [environmentList, subProductList, productList, productVocab, subProductVocab, requestOptions, authOptions, data.ciJobConfigId, requestType]);

  const baseModalProps = {
    title: `${_.startCase(_.lowerCase(data?.tool_name || ''))} CI Configuration.`,
    width: MODAL_SIZE_EXTRA_LARGE,
    okText: "Save Config",
    onOk: onSubmit
  }

  return (
    <BaseModal ref={ref} modalProps={baseModalProps} afterClose={afterClose}>
      <Spin
        spinning={loading}
        indicator={<AppLoader style={{ marginTop: 0 }} />}
      >
        <Form layout="vertical" form={form} initialValues={initialFormValues} onValuesChange={onChange}>
          {generateFormFields(fields)}
        </Form>
      </Spin>
    </BaseModal>
  );
})

export default CIConfiguration;

export const deleteCIConfig = (record, callBack=_.noop) => {
  const { data } = record;
  if(data?.ciJobConfigId){
    ModalUtil.show({
      content: <ConfirmPopup
          type="delete"
          title={`Are you sure want to Delete 'CI Config'?`}
          confirmText="Delete"
          okText="Yes"
          onOk={async () => {
            await CIConfigAPI.deleteCIConfigJob(data.ciJobConfigId).promise.then(() => {
              message.success('CI configuration deleted successfully.');
            });
        }}
      />,
      afterClose: callBack
    })
  }
};

export const showCIConfig = (record, callBack=_.noop) => {
  const { data, name } = record;
  if (data?.ciJobConfigId && name === "configure") {
    const { promise } = CIConfigAPI.triggerCIConfigJob(data.ciJobConfigId)
    promise.then(() => {
      message.success('CI Job has been triggered successfully.');
    })
  } else {
    ModalUtil.show({
      content: <CIConfiguration {...{ data }} />,
      afterClose: callBack
    })
  }
};

export const getCIActionOptions = (record) => {
  const txt = _.isUndefined(record.ciJobConfigId) ? "CI Config" : "Trigger CI Job";
  const fields = [
    {
      type: "configure",
      text: txt,
      icon: _.isUndefined(record.ciJobConfigId) ? <SettingIcon /> : <PlayIcon /> ,
      tooltip: txt,
      disabled: false,
      permission: PermissionSetMap.CI_JOB_ADD
    },
    ...(!_.isUndefined(record.ciJobConfigId)
      ? [
        {
          type: "edit_configuration",
          text: "Edit CI Config",
          icon: <EditIcon /> ,
          tooltip: "Edit CI Config",
          disabled: false,
          permission: PermissionSetMap.CI_JOB_UPDATE
        },
        {
          type: "delete_configuration",
          text: "Delete CI Config",
          icon: <DeleteIcon className="danger"/> ,
          tooltip: "Delete CI Config",
          disabled: false,
          permission: PermissionSetMap.CI_JOB_DELETE
        }
      ]
      : [])
  ]
  return fields;
}